// apps/client/src/hooks/pipelineTracker/usePipelineReport.ts
//
// Pipeline Tracker: deal-grain stage report.
//
// The wire types below mirror the payload built by
// services/client-functions/src/pipelineTracker/reports/pipelineReport.ts. They are declared here
// rather than imported because the browser bundle cannot reach into the functions package, and
// because the Pipeline Tracker is fenced off from the rest of the product by CI
// (.github/scripts/check-pipeline-tracker-isolation.sh): a shared package would put a tracker
// concern into code the LMG surface also consumes.
//
// IN FLIGHT HAS ONE DEFINITION: an active canonical stage, no settled date, no not-proceeding
// date. Deals the server could not classify are NOT in these figures; they arrive in `exceptions`
// with their counts and their money, and the tab shows them. A screen that quietly absorbed them
// would be wrong in a way no reader could detect.
//
// MONEY IS INTEGER CENTS on the wire. `formatCents` below is the only place it becomes dollars.
//
// NO PII. The deal table carries no borrower name, no deal name, no referrer person and no
// comments column, so nothing rendered from this payload can leak one.

import { useMemo } from 'react';
import { REPORT_CALLABLE, PIPELINE_REPORT_ACTION } from './reportContract';
import { useTrackerCall } from './reportCache';
export { REPORT_CALLABLE, PIPELINE_REPORT_ACTION } from './reportContract';

/**
 * The tracker report dispatcher, and the action within it.
 *
 * Declared as constants so the callable name lives in exactly one place: the dispatcher is owned
 * elsewhere, and a silent rename would surface as a generic "internal" error with no clue where
 * it came from.
 */

export type DealStageClass = 'active' | 'lost' | 'post_settlement';

/**
 * Entry-cohort conversion. Mirrors milestones.ts on the server.
 *
 * Bucketed on the date a deal was CREATED, so it can see commercial deals that
 * the date-driven flow band cannot: those reach settlement carrying only Date
 * Created and Date Settled, because Salestrekker writes statuses through
 * ApplyOnline and that is residential only.
 */
export interface MilestoneFigure {
  count: number;
  valueCents: number;
  /** Of `count`, how many were established by a real date column. */
  fromDate: number;
  /** Of `count`, how many were established by the deal's current stage. */
  fromStage: number;
}

/**
 * One broker's milestone spread over the same entry cohort. Mirrors milestones.ts.
 *
 * COUNTS AND VALUES, NOT A FUNNEL. `leads` is every deal the broker created in the window and the
 * other four are subsets of it, each established by its own attainment rule. Two of them divided
 * by each other is not a conversion rate, which is why the only percentage this surface shows is a
 * broker's share of a column's total.
 */
export interface AdviserMilestoneRow {
  /** The key the report folds on, matching ClassifiedPerson.adviserKey exactly. */
  adviserKey: string;
  adviserName: string;
  leads: number;
  /** Meetings BOOKED, derived from stage alone. Understates by construction; see the server. */
  meetings: number;
  lodged: number;
  approved: number;
  settled: number;
  /**
   * Integer cents of exactly the deals each count counts. Absent from a payload built before the
   * values existed, in which case the table offers counts only.
   */
  leadsValueCents?: number;
  meetingsValueCents?: number;
  lodgedValueCents?: number;
  approvedValueCents?: number;
  settledValueCents?: number;
}

export interface CohortWindowFigures {
  from: string;
  to: string;
  /** Days the window covers. A short window has had less time to convert. */
  windowDays: number;
  cohortDeals: number;
  cohortValueCents: number;
  /** Absent from a payload built before the spread existed. */
  reachedMeeting?: MilestoneFigure;
  reachedLodged: MilestoneFigure;
  reachedApproved?: MilestoneFigure;
  settled: MilestoneFigure;
  lost: MilestoneFigure;
  /**
   * The same cohort split per broker, heaviest first. Absent from an older payload, in which case
   * the people table renders the four columns it always rendered.
   */
  advisers?: AdviserMilestoneRow[];
  /** Basis points, integer. Null when the denominator is zero. */
  lodgedPerCreatedBp: number | null;
  settledPerLodgedBp: number | null;
  settledPerCreatedBp: number | null;
}

export interface CohortFigures extends CohortWindowFigures {
  period: PeriodKey;
}

/** Period-to-date windows. Mirrors periods.ts on the server. */
export type PeriodKey = 'wtd' | 'mtd' | 'qtd' | 'fytd' | 'cytd';

/**
 * What the bands are currently showing.
 *
 * 'custom' is a UI SELECTION, not a period. The server never receives it: a custom window
 * travels as an explicit pair of dates beside the rolling windows, and widening PeriodKey
 * itself would have let 'custom' reach code that expects a window it can derive.
 */
export type WindowSelection = PeriodKey | 'custom';

export interface FlowFigure {
  count: number;
  /** Integer cents. */
  valueCents: number;
}

/**
 * What happened inside one window.
 *
 * FLOW, never stock. `from` and `to` are inclusive and `to` is always the
 * SNAPSHOT date, so the UI can print the window rather than a bare label that
 * leaves the reader guessing whether it ran to the export or to today.
 */
export interface WindowFigures {
  from: string;
  to: string;
  created: FlowFigure;
  lodged: FlowFigure;
  approved: FlowFigure;
  settled: FlowFigure;
  lost: FlowFigure;
}

export interface PeriodFigures extends WindowFigures {
  period: PeriodKey;
}

/** One option a filter dropdown can offer, with how many deals carry it. */
export interface FilterOption {
  name: string;
  count: number;
}

export interface StageFigures {
  canonicalKey: string;
  label: string;
  order: number;
  class: DealStageClass;
  staleAfterDays: number | null;
  dealCount: number;
  /** Integer cents. */
  totalValueCents: number;
  medianAgeDays: number | null;
  p75AgeDays: number | null;
  staleCount: number;
  criticalCount: number;
  /** Cumulative fixed buckets: every over-90 deal is also an over-30 deal. */
  over30Count: number;
  over90Count: number;
  /**
   * Distinct raw provider spellings that resolved into this canonical stage.
   * 1 means the provider uses one name; higher means the taxonomy is merging a
   * dialect, which is the fact worth putting in front of the practice.
   */
  rawNameCount: number;
  /** Disjoint ageing bands, for the age-in-stage matrix. */
  age0to7: number;
  age8to30: number;
  age31to90: number;
  age90plus: number;
  unknownAgeCount: number;
}

export interface TerminalFigures {
  settledCount: number;
  settledValueCents: number;
  lostCount: number;
  lostValueCents: number;
}

export interface LossReasonFigures {
  category: string;
  /**
   * Whether this row is a WHY or a WHERE. Mirrors pipelineReport.ts.
   *
   * 'active_stage_at_loss' means the deal died while still sitting in an active stage, so the
   * label is the stage it was in, not a reason. Salestrekker never moves a dead deal, so for a
   * Salestrekker tenant this is almost every loss, and rendering it as a reason tells a broker
   * something that did not happen.
   */
  basis: 'category' | 'lost_stage' | 'active_stage_at_loss' | 'not_recorded';
  dealCount: number;
  totalValueCents: number;
}

export interface AdviserOption {
  adviserId: string | null;
  adviserName: string;
  inFlightCount: number;
  totalCount: number;
}

/**
 * One person, classified by the tenant's broker roster. Mirrors resolve.ts on the server.
 *
 * The counts are the SAME counts as in `advisers`: a roster decides how people are arranged on
 * the page, never what their deals add up to.
 */
export interface ClassifiedPerson extends AdviserOption {
  /** The key the report folds on: owner ref when present, display name when not. */
  adviserKey: string;
  disposition: 'broker' | 'support' | 'group';
  groupKey: string | null;
  /** True when no roster entry named this person and the default decided it. */
  unclassified: boolean;
}

/** An acquired or third-party book, rolled up to one row and expandable to its members. */
export interface ClassifiedGroup {
  key: string;
  label: string;
  members: ClassifiedPerson[];
  inFlightCount: number;
  totalCount: number;
}

export interface ClassifiedPeople {
  brokers: ClassifiedPerson[];
  groups: ClassifiedGroup[];
  support: ClassifiedPerson[];
  /** People nobody has classified. A prompt for the settings screen, not an error. */
  unclassifiedCount: number;
  /** True when no roster has been saved, so the page should offer to set one up. */
  isSeed: boolean;
}

/** Mirrors processingTimes.ts on the server. Days between two dated milestones. */
export interface ProcessingIntervalFigures {
  key: 'submitted_to_conditional' | 'conditional_to_unconditional' | 'unconditional_to_settled';
  /** Deals where both dates are present and in order. The statistic's denominator. */
  measuredDeals: number;
  medianDays: number | null;
  meanDays: number | null;
  p75Days: number | null;
  maxDays: number | null;
  /** Pairs where the later milestone is dated before the earlier one. Excluded and counted. */
  disorderedDeals: number;
  unmeasurableDeals: number;
}

export interface ProcessingTimesData {
  intervals: ProcessingIntervalFigures[];
  totalDeals: number;
}

export interface DataQualityExceptions {
  unmappedStageDeals: number;
  missingStageDeals: number;
  contradictoryTerminalDeals: number;
  /**
   * Deals whose not-proceeding or settled date decided the outcome because their stage was still
   * an active one. Non-zero only for a tenant on the date_wins terminal-date policy. NOT an
   * exclusion: these deals are inside the settled and lost figures.
   */
  terminalDateOverrides: number;
  excludedDeals: number;
  excludedValueCents: number;
  missingStageEnteredDate: number;
  missingDealValue: number;
  futureStageEnteredDate: number;
  duplicateDealIds: number;
  negativeDealValue: number;
}

export interface AttentionRow {
  dealRef: string;
  adviser: string;
  stageLabel: string;
  /** The provider's own stage name, beside the canonical one. Never a person. */
  rawStageName: string | null;
  valueCents: number | null;
  ageDays: number;
  bucket: 'stale' | 'critical';
  staleAfterDays: number;
}

export interface UnmappedStageName {
  raw: string;
  workflow: string;
  count: number;
  normalisedRaw: string;
  reason: string;
  danglingCanonicalKey?: string;
}

export interface PipelineReportProvenance {
  asAtDate: string;
  snapshotUploadId: string;
  taxonomyIsSeed: boolean;
  taxonomyInvalidReason?: string;
  adviserFilter: { adviserId: string; adviserName: string } | null;
  inFlightNote: string;
  exclusionNote: string;
  stalenessNote: string;
}

export interface PipelineReportPayload {
  stages: StageFigures[];
  totals: {
    inFlightDeals: number;
    inFlightValueCents: number;
    staleDeals: number;
    criticalDeals: number;
    over30Deals: number;
    over90Deals: number;
    medianAgeDays: number | null;
    p75AgeDays: number | null;
  };
  terminal: TerminalFigures;
  lossReasons: LossReasonFigures[];
  advisers: AdviserOption[];
  /**
   * The same people, arranged by the broker roster. Absent from an older payload, in which case
   * the page falls back to the flat list it always rendered.
   */
  people?: ClassifiedPeople;
  exceptions: DataQualityExceptions;
  unmappedStageNames: UnmappedStageName[];
  stageCoverage: { mappedDeals: number; unmappedDeals: number; coveragePct: number };
  /** All five windows, computed together so the selector switches with no refetch. */
  periods?: PeriodFigures[];
  cohorts?: CohortFigures[];
  /**
   * The same two measurements over the window the operator chose, when they chose one.
   *
   * Absent unless a window was asked for, which is why the bands fall back to the rolling
   * selection rather than rendering an empty custom view: an older payload, or a report
   * fetched before the dates were applied, simply does not carry these.
   */
  customPeriod?: WindowFigures;
  customCohort?: CohortWindowFigures;
  /** Days between dated milestones. Absent from an older payload. */
  processing?: ProcessingTimesData;
  /** Deal types and labels present in the snapshot, heaviest first. */
  workflows?: FilterOption[];
  labels?: FilterOption[];
  attention: AttentionRow[];
  attentionTotal: number;
  provenance: PipelineReportProvenance;
}

export type PipelineReportResponse =
  | ({ status: 'ok' } & PipelineReportPayload)
  | { status: 'no_snapshot'; asAtDate: string | null; reason: 'never_ingested' | 'not_committed' }
  | { status: 'invalid_request'; reason: string };

/**
 * Integer cents to a displayed amount, in one place.
 *
 * The ONLY division by 100 on this surface. A 100x error reached production emails once, and it
 * did so because the conversion happened in more than one place and one of them was wrong.
 * Whole dollars: a deal book rendered to the cent is noise, not precision.
 */
export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return 'Not recorded';
  return (cents / 100).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  });
}

/** '2026-08-29' to '29 Aug 2026'. Parsed as UTC so a label cannot slip a day. */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return 'Unknown';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export interface UsePipelineReportParams {
  companyId: string;
  /** Snapshot to read. Omitted means the most recently committed snapshot. */
  asAtDate?: string;
  /** Restrict the figures to one adviser. The adviser LIST comes back unfiltered regardless. */
  adviserId?: string;
  /** Scope to one broker-roster group. Ignored by the server when adviserId is also set. */
  groupKey?: string;
  /** Restrict to one Salestrekker workflow, shown to the operator as "deal type". */
  workflowName?: string;
  /** Restrict to deals carrying one label. Whole label, never a substring. */
  dealLabel?: string;
  /**
   * An explicit window for the period and cohort bands, inclusive at both ends.
   *
   * Both or neither: the server refuses a half-stated window, so sending one alone would
   * spend a round trip to be told what the caller already knows. Unlike the five rolling
   * windows, which arrive together and switch with no refetch, changing these refetches,
   * because only the server knows which deals fall inside dates it has not seen before.
   */
  windowStart?: string;
  windowEnd?: string;
}

export interface UsePipelineReportReturn {
  response: PipelineReportResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Load the deal-grain stage report for one committed snapshot.
 *
 * `no_snapshot` comes back as DATA, not as an error: a tenant who has the feature but has not
 * uploaded anything yet is in a normal first-run state, and an error banner there would read like
 * a fault the operator has to report.
 */
export function usePipelineReport({
  companyId,
  asAtDate,
  adviserId,
  groupKey,
  workflowName,
  dealLabel,
  windowStart,
  windowEnd,
}: UsePipelineReportParams): UsePipelineReportReturn {
  // Absent fields are omitted rather than sent as `undefined`: the callable transport serialises
  // an undefined field as JSON null, which the server's ISO-date check rejects.
  const request = useMemo(
    () =>
      companyId
        ? {
            action: PIPELINE_REPORT_ACTION,
            companyId,
            ...(asAtDate ? { asAtDate } : {}),
            ...(adviserId ? { adviserId } : {}),
            ...(groupKey ? { groupKey } : {}),
            ...(workflowName ? { workflowName } : {}),
            ...(dealLabel ? { dealLabel } : {}),
            // Both or neither, enforced here too: sending one half would be refused by the
            // server, and the refusal would surface as an error banner over a page that is
            // simply mid-edit while the operator types the second date.
            ...(windowStart && windowEnd ? { windowStart, windowEnd } : {}),
          }
        : null,
    [companyId, asAtDate, adviserId, groupKey, workflowName, dealLabel, windowStart, windowEnd]
  );
  const { data, loading, error, refetch } = useTrackerCall<PipelineReportResponse>(
    REPORT_CALLABLE,
    request,
    {
      label: 'usePipelineReport',
      // Surfaced, never swallowed. A silent empty table here reads as "there is no pipeline",
      // which is the most misleading thing this report could say.
      describeError: (err) =>
        err instanceof Error ? err.message : 'Failed to load the pipeline report',
    }
  );

  return { response: error ? null : data, loading, error, refetch };
}
